import type { StepInputRendererProps } from "./StepInputRenderer";

import { formatPhoneDisplay } from "@homeinstead/shared";
import { cn } from "@homeinstead/ui";
import { Button } from "@homeinstead/ui/components/Button";
import Image from "next/image";
import { TrackedPhoneLink } from "~www/components/TrackedPhoneLink";
import { urlFor } from "~www/lib/sanity/sanity-images";
import {
  FALLBACK_PHONE_NUMBER,
  getPhoneFromContext,
} from "~www/lib/site-builder/get-phone-from-context";
import {
  COLOR_VARIANT_MAP,
  FONT_WEIGHT_CLASS,
  WIDTH_VARIANT_CLASS,
} from "./button-styles";

const DEFAULT_LABEL = "Call Us: {{phoneNumber}}";
const ERROR_CONTEXT = "LeadFormPhoneLinkCtaFormButton";

export function LeadFormPhoneLinkCtaFormButton({
  input,
  phoneNumber,
}: StepInputRendererProps) {
  if (input._type !== "phoneLinkCta") {
    console.error({
      message: `${ERROR_CONTEXT} - incorrect input type provided`,
    });
    return null;
  }
  const resolvedPhoneNumber =
    phoneNumber ?? getPhoneFromContext(undefined, FALLBACK_PHONE_NUMBER);

  const labelTemplate = input.buttonLabel ?? DEFAULT_LABEL;
  const label = labelTemplate.replaceAll(
    "{{phoneNumber}}",
    formatPhoneDisplay(resolvedPhoneNumber),
  );
  const showPhoneIcon = input.icon === "phone";
  const customIconUrl =
    input.icon === "custom" && input.customIcon
      ? urlFor(input.customIcon).url()
      : null;

  return (
    <Button
      asChild
      className={cn(
        input.widthVariant
          ? WIDTH_VARIANT_CLASS[input.widthVariant]
          : WIDTH_VARIANT_CLASS.full,
        {
          "gap-2": showPhoneIcon || Boolean(customIconUrl),
          [FONT_WEIGHT_CLASS.regular]: input.fontWeight === "regular",
          [FONT_WEIGHT_CLASS.semibold]: input.fontWeight === "semibold",
          [FONT_WEIGHT_CLASS.bold]: input.fontWeight === "bold",
        },
      )}
      type="button"
      variant={
        input.colorVariant ? COLOR_VARIANT_MAP[input.colorVariant] : undefined
      }
    >
      <TrackedPhoneLink
        className="no-underline"
        phoneNumber={resolvedPhoneNumber}
        placement="lead form"
        size="inline"
        variant="plain"
        withIcon={showPhoneIcon}
      >
        {!customIconUrl ? (
          label
        ) : (
          <>
            <Image
              alt="Phone CTA icon"
              className="size-5"
              height={20}
              src={customIconUrl}
              width={20}
            />
            <span>{label}</span>
          </>
        )}
      </TrackedPhoneLink>
    </Button>
  );
}
